import { openDB } from "idb";

const DB_NAME = "WebBuilderDB";
const CANVAS_STORE_NAME = "canvasDivisions";
const PAGES_STORE_NAME = "pages";
const PREFERENCES_STORE_NAME = "preferences";
const FILES_STORE_NAME = "files";
const REUSABLE_COMPONENTS_STORE_NAME = "reusableComponents";
const HISTORY_STACK_STORE_NAME = "history";
const UNDO_REDO_QUEUE_STORE_NAME = "undoRedoQueue";

const DB_VERSION = 4; // Increment the version

class IndexedDBService {
  async openDB() {
    return openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, newVersion, transaction) {
        // Handle all possible upgrade paths
        if (oldVersion < 1) {
          db.createObjectStore(CANVAS_STORE_NAME, {
            keyPath: ["pageId", "id"],
          });
        }
        if (oldVersion < 2) {
          db.createObjectStore(PAGES_STORE_NAME, { keyPath: "id" });
          db.createObjectStore(PREFERENCES_STORE_NAME, { keyPath: "key" });
        }
        if (oldVersion < 3) {
          db.createObjectStore(FILES_STORE_NAME, { keyPath: "id" });
        }
        if (oldVersion < 4) {
          db.createObjectStore(REUSABLE_COMPONENTS_STORE_NAME, {
            keyPath: "id",
          });
          db.createObjectStore(HISTORY_STACK_STORE_NAME, {
            keyPath: "id",
            autoIncrement: true,
          });
          db.createObjectStore(UNDO_REDO_QUEUE_STORE_NAME, {
            autoIncrement: true,
          });
        }
      },
    });
  }

  async saveCanvasDivisions(pageId, canvasDivisions) {
    const db = await this.openDB();
    const tx = db.transaction(CANVAS_STORE_NAME, "readwrite");
    const store = tx.objectStore(CANVAS_STORE_NAME);

    await store.delete(IDBKeyRange.bound([pageId, 0], [pageId, Infinity]));

    for (const division of canvasDivisions) {
      await store.add({ ...division, pageId });
    }

    await tx.done;
  }

  async getCanvasDivisions(pageId) {
    const db = await this.openDB();
    const tx = db.transaction(CANVAS_STORE_NAME, "readonly");
    const store = tx.objectStore(CANVAS_STORE_NAME);
    const divisions = await store.getAll(
      IDBKeyRange.bound([pageId, 0], [pageId, Infinity])
    );
    return divisions.map(({ pageId, ...division }) => division);
  }

  async savePage(page) {
    const db = await this.openDB();
    const tx = db.transaction(PAGES_STORE_NAME, "readwrite");
    const store = tx.objectStore(PAGES_STORE_NAME);
    await store.put(page);
    await tx.done;
  }

  async getPages() {
    const db = await this.openDB();
    return db.getAll(PAGES_STORE_NAME);
  }

  async deletePage(pageId) {
    const db = await this.openDB();
    const tx = db.transaction(
      [PAGES_STORE_NAME, CANVAS_STORE_NAME],
      "readwrite"
    );
    await tx.objectStore(PAGES_STORE_NAME).delete(pageId);
    await tx
      .objectStore(CANVAS_STORE_NAME)
      .delete(IDBKeyRange.bound([pageId, 0], [pageId, Infinity]));
    await tx.done;
  }

  async clearAllPages() {
    const db = await this.openDB();
    const tx = db.transaction(
      [PAGES_STORE_NAME, CANVAS_STORE_NAME],
      "readwrite"
    );
    await tx.objectStore(PAGES_STORE_NAME).clear();
    await tx.objectStore(CANVAS_STORE_NAME).clear();
    await tx.done;
  }

  async savePreference(key, value) {
    const db = await this.openDB();
    const tx = db.transaction(PREFERENCES_STORE_NAME, "readwrite");
    const store = tx.objectStore(PREFERENCES_STORE_NAME);
    await store.put({ key, value });
    await tx.done;
  }

  async getPreference(key) {
    const db = await this.openDB();
    const tx = db.transaction(PREFERENCES_STORE_NAME, "readonly");
    const store = tx.objectStore(PREFERENCES_STORE_NAME);
    const result = await store.get(key);
    return result ? result.value : null;
  }

  async deletePreference(key) {
    const db = await this.openDB();
    const tx = db.transaction(PREFERENCES_STORE_NAME, "readwrite");
    const store = tx.objectStore(PREFERENCES_STORE_NAME);
    await store.delete(key);
    await tx.done;
  }

  async saveFile(file) {
    const db = await this.openDB();
    const tx = db.transaction(FILES_STORE_NAME, "readwrite");
    const store = tx.objectStore(FILES_STORE_NAME);
    await store.put(file);
    await tx.done;
  }

  async getFiles() {
    const db = await this.openDB();
    const tx = db.transaction(FILES_STORE_NAME, "readonly");
    const store = tx.objectStore(FILES_STORE_NAME);
    const allFiles = await store.getAll();
    return allFiles;
  }

  async deleteFile(fileId) {
    const db = await this.openDB();
    const tx = db.transaction(FILES_STORE_NAME, "readwrite");
    const store = tx.objectStore(FILES_STORE_NAME);
    await store.delete(fileId);
    await tx.done;
  }

  async updateFile(file) {
    const db = await this.openDB();
    const tx = db.transaction(FILES_STORE_NAME, "readwrite");
    const store = tx.objectStore(FILES_STORE_NAME);
    await store.put(file);
    await tx.done;
  }

  async deleteAllFiles() {
    const db = await this.openDB();
    const tx = db.transaction(FILES_STORE_NAME, "readwrite");
    const store = tx.objectStore(FILES_STORE_NAME);

    await store.clear();
    await tx.done;
  }

  async saveReusableComponent(component) {
    const db = await this.openDB();
    const tx = db.transaction(REUSABLE_COMPONENTS_STORE_NAME, "readwrite");
    const store = tx.objectStore(REUSABLE_COMPONENTS_STORE_NAME);
    await store.put(component);
    await tx.done;
  }

  async getReusableComponents() {
    const db = await this.openDB();
    return db.getAll(REUSABLE_COMPONENTS_STORE_NAME);
  }

  async deleteReusableComponent(componentId) {
    const db = await this.openDB();
    const tx = db.transaction(REUSABLE_COMPONENTS_STORE_NAME, "readwrite");
    const store = tx.objectStore(REUSABLE_COMPONENTS_STORE_NAME);
    await store.delete(componentId);
    await tx.done;
  }
  async updateReusableComponent(componentId) {
    const db = await this.openDB();
    await db.put(REUSABLE_COMPONENTS_STORE_NAME, componentId);
  }

  async pushToHistoryStack(action) {
    const db = await this.openDB();
    const tx = db.transaction(
      [HISTORY_STACK_STORE_NAME, PREFERENCES_STORE_NAME],
      "readwrite"
    );
    const historyStore = tx.objectStore(HISTORY_STACK_STORE_NAME);
    const preferencesStore = tx.objectStore(PREFERENCES_STORE_NAME);

    // Get current pointer
    let pointer = await preferencesStore.get("historyPointer");
    let currentPointer = pointer ? pointer.value : -1;

    // Remove future entries if we're not at the end of the stack
    const count = await historyStore.count();
    if (currentPointer < count - 1) {
      const keys = await historyStore.getAllKeys();
      for (let i = currentPointer + 1; i < keys.length; i++) {
        await historyStore.delete(keys[i]);
      }
    }

    // Add new action
    await historyStore.add(action);

    // Update pointer
    currentPointer++;
    await preferencesStore.put({
      key: "historyPointer",
      value: currentPointer,
    });

    await tx.done;
    return currentPointer;
  }

  async getHistoryStack() {
    const db = await this.openDB();
    return db.getAll(HISTORY_STACK_STORE_NAME);
  }

  async getHistoryPointer() {
    const db = await this.openDB();
    const result = await db.get(PREFERENCES_STORE_NAME, "historyPointer");
    return result ? result.value : -1;
  }

  async setHistoryPointer(value) {
    const db = await this.openDB();
    await db.put(PREFERENCES_STORE_NAME, { key: "historyPointer", value });
  }
  async clearHistoryStack() {
    const db = await this.openDB();
    const tx = db.transaction(
      [HISTORY_STACK_STORE_NAME, PREFERENCES_STORE_NAME],
      "readwrite"
    );
    const historyStore = tx.objectStore(HISTORY_STACK_STORE_NAME);
    await historyStore.clear();

    const preferencesStore = tx.objectStore(PREFERENCES_STORE_NAME);
    await preferencesStore.put({ key: "historyPointer", value: -1 });

    await tx.done;
  }
  async pushToUndoRedoQueue(operation) {
    const db = await this.openDB();
    const tx = db.transaction("undoRedoQueue", "readwrite");
    const store = tx.objectStore("undoRedoQueue");
    await store.add(operation);
    await tx.done;
  }

  async getNextUndoRedoOperation() {
    const db = await this.openDB();
    const tx = db.transaction("undoRedoQueue", "readwrite");
    const store = tx.objectStore("undoRedoQueue");
    const cursor = await store.openCursor();
    if (cursor) {
      const operation = cursor.value;
      await cursor.delete();
      await tx.done;
      return operation;
    }
    await tx.done;
    return null;
  }

  async setUndoRedoLock(isLocked) {
    await this.savePreference("undoRedoLock", isLocked);
  }

  async getUndoRedoLock() {
    return await this.getPreference("undoRedoLock");
  }
}

const indexedDBService = new IndexedDBService();
export default indexedDBService;

// import { openDB } from "idb";

// const DB_NAME = "WebBuilderDB";
// const CANVAS_STORE_NAME = "canvasDivisions";
// const PAGES_STORE_NAME = "pages";
// const PREFERENCES_STORE_NAME = "preferences";
// const FILES_STORE_NAME = "files";
// const REUSABLE_COMPONENTS_STORE_NAME = "reusableComponents";

// const DB_VERSION = 4; // Increment the version

// class IndexedDBService {
//   async openDB() {
//     return openDB(DB_NAME, DB_VERSION, {
//       upgrade(db, oldVersion, newVersion, transaction) {
//         // Handle all possible upgrade paths
//         if (oldVersion < 1) {
//           db.createObjectStore(CANVAS_STORE_NAME, {
//             keyPath: ["pageId", "id"],
//           });
//         }
//         if (oldVersion < 2) {
//           db.createObjectStore(PAGES_STORE_NAME, { keyPath: "id" });
//           db.createObjectStore(PREFERENCES_STORE_NAME, { keyPath: "key" });
//         }
//         if (oldVersion < 3) {
//           db.createObjectStore(FILES_STORE_NAME, { keyPath: "id" });
//         }
//         if (oldVersion < 4) {
//           db.createObjectStore(REUSABLE_COMPONENTS_STORE_NAME, {
//             keyPath: "id",
//           });
//         }
//       },
//     });
//   }

//   async saveCanvasDivisions(pageId, canvasDivisions) {
//     const db = await this.openDB();
//     const tx = db.transaction(CANVAS_STORE_NAME, "readwrite");
//     const store = tx.objectStore(CANVAS_STORE_NAME);

//     await store.delete(IDBKeyRange.bound([pageId, 0], [pageId, Infinity]));

//     for (const division of canvasDivisions) {
//       await store.add({ ...division, pageId });
//     }

//     await tx.done;
//   }

//   async getCanvasDivisions(pageId) {
//     const db = await this.openDB();
//     const tx = db.transaction(CANVAS_STORE_NAME, "readonly");
//     const store = tx.objectStore(CANVAS_STORE_NAME);
//     const divisions = await store.getAll(
//       IDBKeyRange.bound([pageId, 0], [pageId, Infinity])
//     );
//     return divisions.map(({ pageId, ...division }) => division);
//   }

//   async savePage(page) {
//     const db = await this.openDB();
//     const tx = db.transaction(PAGES_STORE_NAME, "readwrite");
//     const store = tx.objectStore(PAGES_STORE_NAME);
//     await store.put(page);
//     await tx.done;
//   }

//   async getPages() {
//     const db = await this.openDB();
//     return db.getAll(PAGES_STORE_NAME);
//   }

//   async deletePage(pageId) {
//     const db = await this.openDB();
//     const tx = db.transaction(
//       [PAGES_STORE_NAME, CANVAS_STORE_NAME],
//       "readwrite"
//     );
//     await tx.objectStore(PAGES_STORE_NAME).delete(pageId);
//     await tx
//       .objectStore(CANVAS_STORE_NAME)
//       .delete(IDBKeyRange.bound([pageId, 0], [pageId, Infinity]));
//     await tx.done;
//   }

//   async clearAllPages() {
//     const db = await this.openDB();
//     const tx = db.transaction(
//       [PAGES_STORE_NAME, CANVAS_STORE_NAME],
//       "readwrite"
//     );
//     await tx.objectStore(PAGES_STORE_NAME).clear();
//     await tx.objectStore(CANVAS_STORE_NAME).clear();
//     await tx.done;
//   }

//   async savePreference(key, value) {
//     const db = await this.openDB();
//     const tx = db.transaction(PREFERENCES_STORE_NAME, "readwrite");
//     const store = tx.objectStore(PREFERENCES_STORE_NAME);
//     await store.put({ key, value });
//     await tx.done;
//   }

//   async getPreference(key) {
//     const db = await this.openDB();
//     const tx = db.transaction(PREFERENCES_STORE_NAME, "readonly");
//     const store = tx.objectStore(PREFERENCES_STORE_NAME);
//     const result = await store.get(key);
//     return result ? result.value : null;
//   }

//   async deletePreference(key) {
//     const db = await this.openDB();
//     const tx = db.transaction(PREFERENCES_STORE_NAME, "readwrite");
//     const store = tx.objectStore(PREFERENCES_STORE_NAME);
//     await store.delete(key);
//     await tx.done;
//   }

//   async saveFile(file) {
//     const db = await this.openDB();
//     const tx = db.transaction(FILES_STORE_NAME, "readwrite");
//     const store = tx.objectStore(FILES_STORE_NAME);
//     await store.put(file);
//     await tx.done;
//   }

//   async getFiles() {
//     const db = await this.openDB();
//     const tx = db.transaction(FILES_STORE_NAME, "readonly");
//     const store = tx.objectStore(FILES_STORE_NAME);
//     const allFiles = await store.getAll();
//     return allFiles;
//   }

//   async deleteFile(fileId) {
//     const db = await this.openDB();
//     const tx = db.transaction(FILES_STORE_NAME, "readwrite");
//     const store = tx.objectStore(FILES_STORE_NAME);
//     await store.delete(fileId);
//     await tx.done;
//   }

//   async updateFile(file) {
//     const db = await this.openDB();
//     const tx = db.transaction(FILES_STORE_NAME, "readwrite");
//     const store = tx.objectStore(FILES_STORE_NAME);
//     await store.put(file);
//     await tx.done;
//   }

//   async deleteAllFiles() {
//     const db = await this.openDB();
//     const tx = db.transaction(FILES_STORE_NAME, "readwrite");
//     const store = tx.objectStore(FILES_STORE_NAME);

//     await store.clear();
//     await tx.done;
//   }

//   async saveReusableComponent(component) {
//     const db = await this.openDB();
//     const tx = db.transaction(REUSABLE_COMPONENTS_STORE_NAME, "readwrite");
//     const store = tx.objectStore(REUSABLE_COMPONENTS_STORE_NAME);
//     await store.put(component);
//     await tx.done;
//   }

//   async getReusableComponents() {
//     const db = await this.openDB();
//     return db.getAll(REUSABLE_COMPONENTS_STORE_NAME);
//   }

//   async deleteReusableComponent(componentId) {
//     const db = await this.openDB();
//     const tx = db.transaction(REUSABLE_COMPONENTS_STORE_NAME, "readwrite");
//     const store = tx.objectStore(REUSABLE_COMPONENTS_STORE_NAME);
//     await store.delete(componentId);
//     await tx.done;
//   }
//   async updateReusableComponent(componentId) {
//     const db = await this.openDB();
//     await db.put(REUSABLE_COMPONENTS_STORE_NAME, componentId);
//   }
// }

// const indexedDBService = new IndexedDBService();
// export default indexedDBService;
